import { useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import type { SplitOrderLinePart } from '@/infrastructure/FulfillmentAdminClient';
import {
  amountsByQuantity,
  formatCents,
  initialLineSplitParts,
  LINE_SPLIT_MAX_PARTS,
  LINE_SPLIT_MIN_PARTS,
  describeLineSplitBlock,
  newLineSplitPart,
  lineSplitTracksArrival,
  reviewLineSplit,
  type LineSplitPartDraft,
  type LineSplitPartField,
  type SplittableLine,
} from '@/utils/fulfillmentLineSplit';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';

interface SplitLineModalProps {
  readonly line: SplittableLine & { readonly sku: string };
  /** The order's currency, which the line's amounts are in. */
  readonly currency: string;
  readonly busy: boolean;
  /** The server's last refusal of this split, shown until the operator sends again. */
  readonly refusal?: string | undefined;
  readonly onHide: () => void;
  readonly onConfirm: (parts: SplitOrderLinePart[]) => void;
}

const TITLE_ID = 'split-line-title';

/**
 * One purchase line that arrives in several deliveries, recorded as one line per delivery (SSD-7377), as OMS Center
 * split a V1 line. Each part has its own quantity, amount and tracking number; the parts always hold
 * the line's units and, when amounts are typed, its total to the cent, so a paid order's totals never change. An
 * arrived line splits too (SSD-7426): each part then says whether its units arrived or still await their own scan.
 */
export function SplitLineModal({ line, currency, busy, refusal, onHide, onConfirm }: SplitLineModalProps) {
  // Each row keeps its own key, so removing a middle part never hands its inputs and focus to the next part.
  const nextRowId = useRef(0);
  const newRow = (draft: LineSplitPartDraft) => ({ id: nextRowId.current++, draft });
  const [rows, setRows] = useState(() => initialLineSplitParts(line).map(newRow));
  const drafts = rows.map((row) => row.draft);
  const review = reviewLineSplit(line, drafts);
  const splitBlock = describeLineSplitBlock(line) || review.block;
  const hasTotal = typeof line.total === 'number';
  const tracksArrival = lineSplitTracksArrival(line);
  const filledAmounts = amountsByQuantity(line, drafts);
  const fillAmounts = () =>
    filledAmounts &&
    setRows((current) =>
      current.map((row, at) => ({ ...row, draft: { ...row.draft, amount: filledAmounts[at] ?? row.draft.amount } })),
    );

  const editPart = (index: number, field: LineSplitPartField, value: string) =>
    setRows((current) =>
      current.map((row, at) => (at === index ? { ...row, draft: { ...row.draft, [field]: value } } : row)),
    );
  const setArrived = (index: number, arrived: boolean) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, draft: { ...row.draft, arrived } } : row)));
  const addPart = () => setRows((current) => [...current, newRow(newLineSplitPart(line))]);
  const removePart = (index: number) => setRows((current) => current.filter((_, at) => at !== index));

  const cell = (index: number, field: LineSplitPartField, label: string, disabled = false) => {
    const error = review.errors[index]?.[field];
    return (
      <>
        <Form.Control
          size="sm"
          aria-label={`${label} for part ${index + 1}`}
          value={drafts[index]![field]}
          isInvalid={error !== undefined}
          disabled={disabled}
          inputMode={field === 'quantity' ? 'numeric' : field === 'amount' ? 'decimal' : 'text'}
          onChange={(event) => editPart(index, field, event.target.value)}
        />
        {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
      </>
    );
  };

  return (
    <Modal {...ULTIMATE_MODAL} show onHide={onHide} centered size="xl" aria-labelledby={TITLE_ID}>
      <Modal.Header closeButton>
        <Modal.Title id={TITLE_ID}>
          Split line — {line.sku} (line {line.lineNo})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          Line {line.lineNo} holds <strong>{line.totalQuantity}</strong> unit(s)
          {hasTotal && (
            <>
              {' '}
              · total{' '}
              <strong>
                {currency} {formatCents(Math.round(line.total! * 100))}
              </strong>
            </>
          )}
          . Part 1 stays on line {line.lineNo}; every other part becomes a new line of this order.
        </p>
        {tracksArrival && (
          <Alert variant="info" role="note" className="py-2 small" data-testid="split-arrival-note">
            Line {line.lineNo} has already arrived, so its {line.totalQuantity} unit(s) are counted in stock. Mark each
            part as arrived when its parcel is in the warehouse: its units stay in stock. A part not arrived yet takes
            its units out of stock and waits for its own waybill scan to inbound them.
          </Alert>
        )}
        <Table size="sm" bordered className="align-middle mb-2">
          <thead>
            <tr>
              <th style={{ width: '3rem' }}>Part</th>
              <th style={{ width: '9rem' }}>Quantity</th>
              <th style={{ width: '10rem' }}>Amount ({currency})</th>
              <th style={{ width: '10rem' }}>Size</th>
              <th>Tracking number</th>
              {tracksArrival && <th style={{ width: '12rem' }}>Arrived</th>}
              <th style={{ width: '5rem' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id}>
                <td className="text-center">{index + 1}</td>
                <td>{cell(index, 'quantity', 'Quantity')}</td>
                <td>{cell(index, 'amount', 'Amount', !hasTotal)}</td>
                <td>{cell(index, 'size', 'Size')}</td>
                <td>{cell(index, 'trackingNumber', 'Tracking number')}</td>
                {tracksArrival && (
                  <td>
                    <Form.Select
                      size="sm"
                      aria-label={`Arrived for part ${index + 1}`}
                      value={row.draft.arrived ? 'arrived' : 'awaiting'}
                      onChange={(event) => setArrived(index, event.target.value === 'arrived')}
                    >
                      <option value="arrived">Arrived (in stock)</option>
                      <option value="awaiting">Not arrived (awaiting inbound)</option>
                    </Form.Select>
                  </td>
                )}
                <td>
                  {/* The modal mounts outside the page sheet, so its buttons take bootstrap's own variants. */}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    aria-label={`Remove part ${index + 1}`}
                    disabled={drafts.length <= LINE_SPLIT_MIN_PARTS}
                    onClick={() => removePart(index)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="d-flex gap-2 mb-3">
          <Button
            variant="outline-primary"
            size="sm"
            disabled={drafts.length >= LINE_SPLIT_MAX_PARTS}
            onClick={addPart}
          >
            Add part
          </Button>
          {hasTotal && (
            <Button
              variant="outline-primary"
              size="sm"
              disabled={filledAmounts === undefined}
              title={filledAmounts === undefined ? "Enter quantities that hold the line's units first" : undefined}
              onClick={fillAmounts}
            >
              Fill amounts by quantity
            </Button>
          )}
        </div>
        <div className="mb-2" data-testid="split-remaining" aria-live="polite">
          Remaining: <strong>{review.remainingQuantity}</strong> unit(s)
          {review.remainingAmountCents !== undefined && (
            <>
              {' '}
              ·{' '}
              <strong>
                {currency} {formatCents(review.remainingAmountCents)}
              </strong>
            </>
          )}
        </div>
        <Form.Text muted as="div">
          {hasTotal
            ? "Leave every amount blank to share the line's amount by quantity, or fill them by quantity and adjust. Typed amounts must add up to the line's total."
            : 'This line records no amount, so it splits by quantity only.'}{' '}
          A tracking number is optional; leave it blank when the part&apos;s waybill is not known yet. Size is the text
          the order shows for each part (for example 1pack once a 3packs line is split); the product stays the same.
        </Form.Text>
        {refusal && (
          <Alert variant="danger" className="mt-3 mb-0" role="alert">
            {refusal}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {splitBlock && <span className="text-muted small me-auto">{splitBlock}</span>}
        <Button variant="outline-secondary" onClick={onHide}>
          Keep the line
        </Button>
        <Button
          variant="primary"
          disabled={busy || splitBlock !== '' || review.parts === undefined}
          title={splitBlock}
          onClick={() => splitBlock === '' && review.parts && onConfirm(review.parts)}
        >
          Split line
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
